import { INFERRED_TYPE_ENUM } from './chart-channels';

/**
 * An enumeration of the subset of Vega aggregates we support.
 * For a full list of things we could add, see:
 *
 * @see https://vega.github.io/vega/docs/transforms/aggregate/
 */
const AGGREGATE_FUNCTION_ENUM = Object.freeze({
  NONE: '(none)',
  COUNT: 'count',
  DISTINCT: 'distinct',
  SUM: 'sum',
  MEAN: 'mean',
  VARIANCE: 'variance',
  VARIANCEP: 'variancep',
  STDEV: 'stdev',
  STDEVP: 'stdevp',
  // MEDIAN: 'median',
  // Q1: 'q1',           not supported in agg framework, comment out for now
  // Q3: 'q3',
  MIN: 'min',
  MAX: 'max',
});

/**
 * An enumeration subset of the aggregate function enum for specific inferred
 * types.
 **/
const AGGREGATE_FUNCTION_FOR_INFERRED_TYPE_ENUM = Object.freeze({
  [INFERRED_TYPE_ENUM.DATE]: {
    COUNT: AGGREGATE_FUNCTION_ENUM.COUNT,
    DISTINCT: AGGREGATE_FUNCTION_ENUM.DISTINCT,
  },
  [INFERRED_TYPE_ENUM.STRING]: {
    COUNT: AGGREGATE_FUNCTION_ENUM.COUNT,
    DISTINCT: AGGREGATE_FUNCTION_ENUM.DISTINCT,
  },
  [INFERRED_TYPE_ENUM.NUMBER]: {
    SUM: AGGREGATE_FUNCTION_ENUM.SUM,
    COUNT: AGGREGATE_FUNCTION_ENUM.COUNT,
    DISTINCT: AGGREGATE_FUNCTION_ENUM.DISTINCT,
    MEAN: AGGREGATE_FUNCTION_ENUM.MEAN,
    MIN: AGGREGATE_FUNCTION_ENUM.MIN,
    MAX: AGGREGATE_FUNCTION_ENUM.MAX,
    VARIANCE: AGGREGATE_FUNCTION_ENUM.VARIANCE,
    VARIANCEP: AGGREGATE_FUNCTION_ENUM.VARIANCEP,
    STDEV: AGGREGATE_FUNCTION_ENUM.STDEV,
    STDEVP: AGGREGATE_FUNCTION_ENUM.STDEVP,
  },
});

/**
 * An enumeration subset of the aggregate function enum specifying the default
 * aggregation function to use for the inferredType when mapping a field to a channel.
 **/
const DEFAULT_AGGREGATE_FUNCTION_FOR_INFERRED_TYPE_ENUM = Object.freeze({
  [INFERRED_TYPE_ENUM.DATE]: AGGREGATE_FUNCTION_ENUM.COUNT,
  [INFERRED_TYPE_ENUM.STRING]: AGGREGATE_FUNCTION_ENUM.COUNT,
  [INFERRED_TYPE_ENUM.NUMBER]: AGGREGATE_FUNCTION_ENUM.SUM,
});

/**
 * Aggregations across documents wrapped as JavaScript function. The shape of
 * an aggregation is defined as:
 *
 *    {
 *      groupStage: {...},     // always required
 *      projectStage: {...}    // optional
 *    }
 *
 * If the aggregation only requires a single stage (group accumulators like
 * sum, mean, ...) then just return the object with a `groupStage` value.
 *
 * If the aggregation requires a subsequent $project stage, (e.g. distinct,
 * which needs an $addToSet followed by a $size projection), return an object
 * with both `groupStage` and `projectStage` values.
 */
const AGGREGATIONS = Object.freeze({
  [AGGREGATE_FUNCTION_ENUM.COUNT]: function(field) {
    return {
      groupStage: {
        $sum:
          field === '_id'
            ? 1
            : {
                $cond: [{ $ne: [{ $type: `$${field}` }, 'missing'] }, 1, 0],
              },
      },
    };
  },
  /**
   * Returns the number of distinct values per group. This operation requires
   * a $group and $project stage, therefore returning array of two elements.
   *
   * @param {String} field   field name to operate on
   * @returns {Object}       aggregation pipeline operators
   */
  [AGGREGATE_FUNCTION_ENUM.DISTINCT]: function(field) {
    return {
      groupStage: {
        $addToSet: `$${field}`,
      },
      projectStage: {
        $size: `$${field}`,
      },
    };
  },
  [AGGREGATE_FUNCTION_ENUM.SUM]: function(field) {
    return {
      groupStage: {
        $sum: `$${field}`,
      },
    };
  },
  [AGGREGATE_FUNCTION_ENUM.MEAN]: function(field) {
    return {
      groupStage: {
        $avg: `$${field}`,
      },
    };
  },
  [AGGREGATE_FUNCTION_ENUM.MIN]: function(field) {
    return {
      groupStage: {
        $min: `$${field}`,
      },
    };
  },
  [AGGREGATE_FUNCTION_ENUM.MAX]: function(field) {
    return {
      groupStage: {
        $max: `$${field}`,
      },
    };
  },
  [AGGREGATE_FUNCTION_ENUM.STDEV]: function(field) {
    return {
      groupStage: {
        $stdDevSamp: `$${field}`,
      },
    };
  },
  /**
   * Returns the variance over the samples per group. This operation requires
   * a $group and $project stage, therefore returning array of two elements.
   *
   * @param {String} field   field name to operate on
   * @returns {Object}       aggregation pipeline operators
   */
  [AGGREGATE_FUNCTION_ENUM.VARIANCE]: function(field) {
    return {
      groupStage: {
        $stdDevSamp: `$${field}`,
      },
      projectStage: {
        $pow: [`$${field}`, 2],
      },
    };
  },
  [AGGREGATE_FUNCTION_ENUM.STDEVP]: function(field) {
    return {
      groupStage: {
        $stdDevPop: `$${field}`,
      },
    };
  },
  /**
   * Returns the population variance per group. This operation requires
   * a $group and $project stage, therefore returning array of two elements.
   *
   * @param {String} field   field name to operate on
   * @returns {Object}       aggregation pipeline operators
   */
  [AGGREGATE_FUNCTION_ENUM.VARIANCEP]: function(field) {
    return {
      groupStage: {
        $stdDevPop: `$${field}`,
      },
      projectStage: {
        $pow: [`$${field}`, 2],
      },
    };
  },
});

export {
  AGGREGATE_FUNCTION_ENUM,
  AGGREGATE_FUNCTION_FOR_INFERRED_TYPE_ENUM,
  DEFAULT_AGGREGATE_FUNCTION_FOR_INFERRED_TYPE_ENUM,
  AGGREGATIONS,
};
